// Wraps a Firestore read in a timeout + retry, matching the behavior in the
// Redux data loader (dataSlice). Direct reads on the critical loading path
// (e.g. event form questions) otherwise have no timeout, so a stalled
// connection hangs the page indefinitely instead of recovering on a retry.
export const FIRESTORE_TIMEOUT_MS = 8000;

export async function withFirestoreTimeout<T>(
  fn: () => Promise<T>,
  options?: { timeoutMs?: number; retries?: number }
): Promise<T> {
  const timeoutMs = options?.timeoutMs ?? FIRESTORE_TIMEOUT_MS;
  const retries = options?.retries ?? 1;

  const attempt = () =>
    Promise.race([
      fn(),
      new Promise<never>((_resolve, reject) =>
        setTimeout(() => reject(new Error("firestore_timeout")), timeoutMs)
      ),
    ]);

  let lastError: unknown;
  for (let i = 0; i <= retries; i++) {
    try {
      return await attempt();
    } catch (err) {
      lastError = err;
      // Only retry on our timeout; real errors should surface immediately.
      if (!(err instanceof Error && err.message === "firestore_timeout")) {
        throw err;
      }
    }
  }
  throw lastError;
}
